import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  createEntry,
  createPipeline,
  listPipelines,
  replaceStages,
  updatePipeline,
  type Pipeline,
  type PipelineStage,
  createFactor,
  createTag,
  deleteEntry,
  deleteFactor,
  deleteTag,
  getScoreConfig,
  listCatalogue,
  listTags,
  updateEntry,
  updateFactor,
  updateTag,
  type CatalogueEntry,
  type CataloguePath,
  type ScoreFactor,
  type TagEntry,
} from './catalogues-api';
import {
  createCustomField,
  listCustomFields,
  updateCustomField,
  type CustomFieldEntry,
} from './custom-fields-api';

/**
 * The lists a company fills in differently (context.md sections 34, 101).
 *
 * Every section here answers the same question in a different vocabulary, so they share one editor:
 * a name, a colour, a description and a switch. What differs is what the entry means, and that is
 * the part worth writing on the screen rather than leaving somebody to infer.
 *
 * Nothing in use is deletable. The server refuses it and says how many records are affected, which
 * is more useful than a disabled button that does not explain itself.
 */

type Tab = 'statuses' | 'sources' | 'tags' | 'fields' | 'pipelines' | 'reasons' | 'score';

export function CataloguesPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('statuses');

  return (
    <div className="view">
      <PageHeader title={t('catalogues.title')} description={t('catalogues.description')} />

      <div className="tabs" role="tablist">
        {(['statuses', 'sources', 'tags', 'fields', 'pipelines', 'reasons', 'score'] as Tab[]).map(
          (key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className="tab"
              onClick={() => setTab(key)}
            >
              {t(`catalogues.tab.${key}`)}
            </button>
          ),
        )}
      </div>

      {tab === 'statuses' && (
        <CatalogueSection
          path="customer-statuses"
          title={t('catalogues.statuses')}
          hint={t('catalogues.statusesHint')}
          withDefault
        />
      )}
      {tab === 'sources' && (
        <CatalogueSection
          path="customer-sources"
          title={t('catalogues.sources')}
          hint={t('catalogues.sourcesHint')}
        />
      )}
      {tab === 'reasons' && (
        <CatalogueSection
          path="loss-reasons"
          title={t('catalogues.reasons')}
          hint={t('catalogues.reasonsHint')}
          minimal
        />
      )}
      {tab === 'tags' && <TagsSection />}
      {tab === 'fields' && <CustomFieldsSection />}
      {tab === 'pipelines' && <PipelinesSection />}
      {tab === 'score' && <ScoreSection />}
    </div>
  );
}

/** One editor for the three lists that are the same shape on the server. */
function CatalogueSection({
  path,
  title,
  hint,
  withDefault = false,
  minimal = false,
}: {
  path: CataloguePath;
  title: string;
  hint: string;
  withDefault?: boolean;
  minimal?: boolean;
}): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6b7280');
  const [error, setError] = useState<string | null>(null);

  const entries = useQuery({ queryKey: ['catalogue', path], queryFn: () => listCatalogue(path) });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['catalogue', path] });
  };

  const create = useMutation({
    mutationFn: () =>
      createEntry(path, {
        name: name.trim(),
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(minimal ? {} : { description: description.trim() || null, color }),
      }),
    onSuccess: () => {
      setName('');
      setCode('');
      setDescription('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (entry: CatalogueEntry) =>
      updateEntry(path, entry.id, { isActive: !entry.is_active }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const makeDefault = useMutation({
    mutationFn: (entry: CatalogueEntry) => updateEntry(path, entry.id, { isDefault: true }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (entry: CatalogueEntry) => deleteEntry(path, entry.id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<CatalogueEntry>[] = [
    {
      key: 'name',
      header: t('catalogues.name'),
      render: (entry) => (
        <span className="cell-inline">
          {!minimal && <span className="swatch" style={{ background: entry.color }} aria-hidden />}
          <span className="cell-stack">
            <span className="strong">{entry.name}</span>
            {entry.description && <span className="muted small">{entry.description}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'code',
      header: t('catalogues.code'),
      secondary: true,
      render: (entry) => <code>{entry.code}</code>,
    },
    ...(withDefault
      ? [
          {
            key: 'default',
            header: t('catalogues.default'),
            align: 'end' as const,
            render: (entry: CatalogueEntry) =>
              entry.is_default ? (
                <Badge tone="positive">{t('catalogues.isDefault')}</Badge>
              ) : (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => makeDefault.mutate(entry)}
                >
                  {t('catalogues.makeDefault')}
                </button>
              ),
          },
        ]
      : []),
    {
      key: 'status',
      header: t('crm.field.status'),
      align: 'end',
      render: (entry) => (
        <Badge tone={entry.is_active ? 'positive' : 'neutral'}>
          {entry.is_active ? t('catalogues.active') : t('catalogues.inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (entry) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => toggle.mutate(entry)}
          >
            {entry.is_active ? t('catalogues.deactivate') : t('catalogues.activate')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => remove.mutate(entry)}
          >
            {t('catalogues.remove')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={title}>
        <p className="muted small">{hint}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('catalogues.name')} *</span>
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('catalogues.code')}</span>
            <input
              value={code}
              maxLength={40}
              placeholder={t('catalogues.codePlaceholder')}
              onChange={(event) => setCode(event.target.value)}
            />
            <span className="muted small">{t('catalogues.codeHint')}</span>
          </label>

          {!minimal && (
            <>
              <label className="field">
                <span>{t('catalogues.color')}</span>
                <input
                  type="color"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                />
              </label>

              <label className="field">
                <span>{t('catalogues.descriptionLabel')}</span>
                <input
                  value={description}
                  maxLength={500}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </label>
            </>
          )}
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('catalogues.add')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="catalogues"
          rows={entries.data}
          columns={columns}
          rowKey={(entry) => entry.id}
          isLoading={entries.isLoading}
          error={entries.error}
          onRetry={() => void entries.refetch()}
          empty={{ title: t('catalogues.empty'), description: t('catalogues.emptyBody') }}
        />
      </Panel>
    </>
  );
}

function TagsSection(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6b7280');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const tags = useQuery({ queryKey: ['catalogue', 'tags'], queryFn: listTags });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['catalogue', 'tags'] });
  };

  const create = useMutation({
    mutationFn: () =>
      createTag({ name: name.trim(), color, description: description.trim() || null }),
    onSuccess: () => {
      setName('');
      setDescription('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (tag: TagEntry) => updateTag(tag.id, { isActive: !tag.is_active }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (tag: TagEntry) => deleteTag(tag.id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<TagEntry>[] = [
    {
      key: 'name',
      header: t('catalogues.tag'),
      render: (tag) => (
        <span className="cell-inline">
          <span className="swatch" style={{ background: tag.color }} aria-hidden />
          <span className="cell-stack">
            <span className="strong">{tag.name}</span>
            {tag.description && <span className="muted small">{tag.description}</span>}
          </span>
        </span>
      ),
    },
    { key: 'slug', header: 'Slug', secondary: true, render: (tag) => <code>{tag.slug}</code> },
    {
      key: 'usage',
      header: t('catalogues.usage'),
      align: 'end',
      render: (tag) => String(tag.usage),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      align: 'end',
      render: (tag) => (
        <Badge tone={tag.is_active ? 'positive' : 'neutral'}>
          {tag.is_active ? t('catalogues.active') : t('catalogues.inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (tag) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => toggle.mutate(tag)}
          >
            {tag.is_active ? t('catalogues.deactivate') : t('catalogues.activate')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => remove.mutate(tag)}
          >
            {t('catalogues.remove')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('catalogues.tags')}>
        <p className="muted small">{t('catalogues.tagsHint')}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('catalogues.name')} *</span>
            <input value={name} maxLength={60} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('catalogues.color')}</span>
            <input type="color" value={color} onChange={(event) => setColor(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('catalogues.descriptionLabel')}</span>
            <input
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('catalogues.add')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="catalogues-2"
          rows={tags.data?.tags}
          columns={columns}
          rowKey={(tag) => tag.id}
          isLoading={tags.isLoading}
          error={tags.error}
          onRetry={() => void tags.refetch()}
          empty={{ title: t('catalogues.noTags'), description: t('catalogues.noTagsBody') }}
        />
      </Panel>
    </>
  );
}

const FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'INTEGER',
  'DECIMAL',
  'CURRENCY',
  'DATE',
  'DATETIME',
  'BOOLEAN',
  'SELECT',
  'MULTISELECT',
  'EMAIL',
  'PHONE',
  'URL',
];

const ENTITIES = ['COMPANY', 'CONTACT', 'LEAD', 'OPPORTUNITY'];

function CustomFieldsSection(): React.JSX.Element {
  const client = useQueryClient();
  const [entity, setEntity] = useState('COMPANY');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('TEXT');
  const [choices, setChoices] = useState('');
  const [required, setRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fields = useQuery({
    queryKey: ['catalogue', 'custom-fields', entity],
    queryFn: () => listCustomFields(entity),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['catalogue', 'custom-fields', entity] });
  };

  const create = useMutation({
    mutationFn: () =>
      createCustomField({
        entityType: entity,
        label: label.trim(),
        fieldType,
        required,
        ...(choices.trim()
          ? {
              choices: choices
                .split(',')
                .map((choice) => choice.trim())
                .filter(Boolean),
            }
          : {}),
      }),
    onSuccess: () => {
      setLabel('');
      setChoices('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (field: CustomFieldEntry) =>
      updateCustomField(field.id, { entityType: entity, isActive: !field.is_active }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<CustomFieldEntry>[] = [
    {
      key: 'label',
      header: t('catalogues.field'),
      render: (field) => (
        <span className="cell-stack">
          <span className="strong">{field.label}</span>
          <span className="muted small">
            <code>{field.key}</code>
          </span>
        </span>
      ),
    },
    { key: 'type', header: t('catalogues.type'), render: (field) => field.field_type },
    {
      key: 'choices',
      header: t('catalogues.options'),
      secondary: true,
      render: (field) => (field.choices.length > 0 ? field.choices.join(', ') : '—'),
    },
    {
      key: 'required',
      header: t('catalogues.required'),
      align: 'end',
      render: (field) => (field.required ? t('catalogues.yes') : '—'),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      align: 'end',
      render: (field) => (
        <Badge tone={field.is_active ? 'positive' : 'neutral'}>
          {field.is_active ? t('catalogues.active') : t('catalogues.inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (field) => (
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => toggle.mutate(field)}
        >
          {field.is_active ? t('catalogues.deactivate') : t('catalogues.activate')}
        </button>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('catalogues.fields')}>
        <p className="muted small">{t('catalogues.fieldsHint')}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('catalogues.entity')}</span>
            <select
              className="select"
              value={entity}
              onChange={(event) => setEntity(event.target.value)}
            >
              {ENTITIES.map((value) => (
                <option key={value} value={value}>
                  {t(`catalogues.entity.${value}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>{t('catalogues.fieldName')} *</span>
            <input
              value={label}
              maxLength={120}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>

          <label className="field">
            <span>{t('catalogues.type')} *</span>
            <select
              className="select"
              value={fieldType}
              onChange={(event) => setFieldType(event.target.value)}
            >
              {FIELD_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`catalogues.fieldType.${value}`)}
                </option>
              ))}
            </select>
          </label>

          {(fieldType === 'SELECT' || fieldType === 'MULTISELECT') && (
            <label className="field">
              <span>{t('catalogues.options')} *</span>
              <input
                value={choices}
                placeholder="Bronze, Prata, Ouro"
                onChange={(event) => setChoices(event.target.value)}
              />
            </label>
          )}

          <label className="field field--inline">
            <input
              type="checkbox"
              checked={required}
              onChange={(event) => setRequired(event.target.checked)}
            />
            <span>{t('catalogues.required')}</span>
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={label.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('catalogues.add')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="catalogues-3"
          rows={fields.data?.fields}
          columns={columns}
          rowKey={(field) => field.id}
          isLoading={fields.isLoading}
          error={fields.error}
          onRetry={() => void fields.refetch()}
          empty={{ title: t('catalogues.noFields'), description: t('catalogues.noFieldsBody') }}
        />
      </Panel>
    </>
  );
}

function ScoreSection(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('engagement');
  const [points, setPoints] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const config = useQuery({ queryKey: ['catalogue', 'score'], queryFn: getScoreConfig });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['catalogue', 'score'] });
  };

  const create = useMutation({
    mutationFn: () =>
      createFactor({ name: name.trim(), category, defaultPoints: Number(points) || 0 }),
    onSuccess: () => {
      setName('');
      setPoints('0');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const changePoints = useMutation({
    mutationFn: (input: { factor: ScoreFactor; points: number }) =>
      updateFactor(input.factor.id, { defaultPoints: input.points }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (factor: ScoreFactor) => updateFactor(factor.id, { isActive: !factor.is_active }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (factor: ScoreFactor) => deleteFactor(factor.id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<ScoreFactor>[] = [
    {
      key: 'key',
      header: t('catalogues.factor'),
      render: (factor) => (
        <span className="cell-stack">
          <span className="strong">{factor.name}</span>
          <span className="muted small">
            <code>{factor.key}</code>
          </span>
        </span>
      ),
    },
    {
      key: 'category',
      header: t('catalogues.category'),
      secondary: true,
      render: (factor) => t(`catalogues.scoreCategory.${factor.category}`),
    },
    {
      key: 'points',
      header: t('catalogues.points'),
      align: 'end',
      render: (factor) => (
        <input
          type="number"
          className="input--points"
          defaultValue={factor.default_points}
          aria-label={t('catalogues.points')}
          onBlur={(event) => {
            const next = Number(event.target.value);

            if (next !== factor.default_points) changePoints.mutate({ factor, points: next });
          }}
        />
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      align: 'end',
      render: (factor) => (
        <Badge tone={factor.is_active ? 'positive' : 'neutral'}>
          {factor.is_active ? t('catalogues.active') : t('catalogues.inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (factor) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => toggle.mutate(factor)}
          >
            {factor.is_active ? t('catalogues.deactivate') : t('catalogues.activate')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => remove.mutate(factor)}
          >
            {t('catalogues.remove')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('catalogues.score')}>
        <p className="muted small">{t('catalogues.scoreHint')}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('catalogues.factorName')} *</span>
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span>{t('catalogues.category')}</span>
            <select
              className="select"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {['engagement', 'finance', 'sales', 'support'].map((value) => (
                <option key={value} value={value}>
                  {t(`catalogues.scoreCategory.${value}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>{t('catalogues.points')}</span>
            <input
              type="number"
              value={points}
              onChange={(event) => setPoints(event.target.value)}
            />
            <span className="muted small">{t('catalogues.pointsHint')}</span>
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('catalogues.add')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="catalogues-4"
          rows={config.data?.factors}
          columns={columns}
          rowKey={(factor) => factor.id}
          isLoading={config.isLoading}
          error={config.error}
          onRetry={() => void config.refetch()}
          empty={{ title: t('catalogues.noFactors'), description: t('catalogues.noFactorsBody') }}
        />
      </Panel>
    </>
  );
}

function PipelinesSection(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, PipelineStage[]>>({});

  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });

  const refresh = (): void => {
    setDraft({});
    void client.invalidateQueries({ queryKey: ['pipelines'] });
  };

  const create = useMutation({
    mutationFn: () =>
      createPipeline({
        name: name.trim(),
        // A funnel with nowhere to win or lose is not a funnel; these are the two it must have.
        stages: [
          { name: 'Novo', probability: 10 },
          { name: 'Ganho', probability: 100, isWon: true },
          { name: 'Perdido', probability: 0, isLost: true },
        ],
      }),
    onSuccess: () => {
      setName('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const makeDefault = useMutation({
    mutationFn: (pipeline: Pipeline) => updatePipeline(pipeline.id, { isDefault: true }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const saveStages = useMutation({
    mutationFn: (pipeline: Pipeline) =>
      replaceStages(
        pipeline.id,
        (draft[pipeline.id] ?? pipeline.stages).map((stage) => ({
          ...(stage.id.startsWith('new-') ? {} : { id: stage.id }),
          name: stage.name,
          probability: stage.probability,
          isWon: stage.is_won,
          isLost: stage.is_lost,
        })),
      ),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const stagesOf = (pipeline: Pipeline): PipelineStage[] => draft[pipeline.id] ?? pipeline.stages;

  const edit = (pipeline: Pipeline, next: PipelineStage[]): void => {
    setDraft((current) => ({ ...current, [pipeline.id]: next }));
  };

  return (
    <>
      <Panel title={t('catalogues.newPipeline')}>
        <p className="muted small">{t('catalogues.pipelinesHint')}</p>

        <div className="form-grid">
          <label className="field">
            <span>{t('catalogues.name')} *</span>
            <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={name.trim() === '' || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('catalogues.add')}
        </button>
      </Panel>

      {(pipelines.data?.pipelines ?? []).map((pipeline) => (
        <Panel
          key={pipeline.id}
          title={pipeline.name}
          actions={
            <span className="cell-inline">
              {pipeline.is_default ? (
                <Badge tone="positive">{t('catalogues.isDefault')}</Badge>
              ) : (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => makeDefault.mutate(pipeline)}
                >
                  {t('catalogues.makeDefault')}
                </button>
              )}
            </span>
          }
        >
          <ol className="dash__chosen">
            {stagesOf(pipeline).map((stage, index) => (
              <li key={stage.id}>
                <span className="cell-inline">
                  <input
                    value={stage.name}
                    aria-label={t('catalogues.stageName')}
                    onChange={(event) =>
                      edit(
                        pipeline,
                        stagesOf(pipeline).map((entry, position) =>
                          position === index ? { ...entry, name: event.target.value } : entry,
                        ),
                      )
                    }
                  />

                  <label className="field field--inline">
                    <span className="muted small">{t('catalogues.probability')}</span>
                    <input
                      type="number"
                      className="input--points"
                      min={0}
                      max={100}
                      value={stage.probability}
                      onChange={(event) =>
                        edit(
                          pipeline,
                          stagesOf(pipeline).map((entry, position) =>
                            position === index
                              ? { ...entry, probability: Number(event.target.value) }
                              : entry,
                          ),
                        )
                      }
                    />
                  </label>

                  {stage.is_won && <Badge tone="positive">{t('catalogues.won')}</Badge>}
                  {stage.is_lost && <Badge tone="danger">{t('catalogues.lost')}</Badge>}

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    aria-label={t('dashboard.moveUp')}
                    disabled={index === 0}
                    onClick={() => {
                      const next = [...stagesOf(pipeline)];

                      if (index === 0) return;
                      [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                      edit(pipeline, next);
                    }}
                  >
                    ↑
                  </button>

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    aria-label={t('dashboard.moveDown')}
                    disabled={index === stagesOf(pipeline).length - 1}
                    onClick={() => {
                      const next = [...stagesOf(pipeline)];

                      if (index === next.length - 1) return;
                      [next[index + 1], next[index]] = [next[index]!, next[index + 1]!];
                      edit(pipeline, next);
                    }}
                  >
                    ↓
                  </button>

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() =>
                      edit(
                        pipeline,
                        stagesOf(pipeline).filter((_, position) => position !== index),
                      )
                    }
                  >
                    {t('catalogues.remove')}
                  </button>
                </span>
              </li>
            ))}
          </ol>

          <div className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() =>
                edit(pipeline, [
                  ...stagesOf(pipeline),
                  {
                    // Marked as new so the save knows to create it rather than look for an id.
                    id: `new-${Date.now()}`,
                    name: t('catalogues.newStage'),
                    position: stagesOf(pipeline).length,
                    probability: 50,
                    is_won: false,
                    is_lost: false,
                    sla_days: null,
                  },
                ])
              }
            >
              + {t('catalogues.addStage')}
            </button>

            <button
              type="button"
              className="button button--primary button--compact"
              disabled={!draft[pipeline.id] || saveStages.isPending}
              onClick={() => saveStages.mutate(pipeline)}
            >
              {t('catalogues.saveStages')}
            </button>
          </div>
        </Panel>
      ))}
    </>
  );
}
